import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { HeaderActionsOverflowMenu, type PatientHeaderAction } from './HeaderActionsOverflowMenu';

interface PatientHeaderActionsProps {
  isArchived: boolean;
  hasFirstConsultation: boolean;
  hasTreatmentPlans: boolean;
  hasSessionNotes: boolean;
  onRestore: () => void;
  onFirstConsultation: () => void;
  onTreatmentPlan: () => void;
  onSessionNote: () => void;
  onEdit: () => void;
  onArchive: () => void;
}

export function PatientHeaderActions({
  isArchived,
  hasFirstConsultation,
  hasTreatmentPlans,
  hasSessionNotes,
  onRestore,
  onFirstConsultation,
  onTreatmentPlan,
  onSessionNote,
  onEdit,
  onArchive,
}: PatientHeaderActionsProps) {
  const { t } = useTranslation('patients');

  if (isArchived === true) {
    return (
      <div className="flex flex-wrap items-start justify-end gap-2">
        <Button variant="outline" size="chip" onClick={onRestore}>
          <Icon name="RefreshCw" /> {t('detail.restore')}
        </Button>
      </div>
    );
  }

  const overflowActions: PatientHeaderAction[] = [];
  if (hasFirstConsultation === true) {
    overflowActions.push({
      key: 'firstConsultation',
      iconName: 'ClipboardCheck',
      label: t('detail.firstConsultation'),
      onSelect: onFirstConsultation,
      isDanger: false,
    });
  }
  if (hasTreatmentPlans === true) {
    overflowActions.push({
      key: 'treatmentPlan',
      iconName: 'Clipboard',
      label: t('detail.treatmentPlan'),
      onSelect: onTreatmentPlan,
      isDanger: false,
    });
  }
  overflowActions.push({
    key: 'edit',
    iconName: 'Edit',
    label: t('detail.edit'),
    onSelect: onEdit,
    isDanger: false,
  });
  overflowActions.push({
    key: 'archive',
    iconName: 'Trash',
    label: t('detail.archive'),
    onSelect: onArchive,
    isDanger: true,
  });

  return (
    <div className="flex items-start justify-end gap-2">
      <div className="hidden [@container(min-width:56rem)]:flex flex-wrap items-start justify-end gap-2">
        {hasFirstConsultation === false ? null : (
          <Button variant="outline" size="chip" onClick={onFirstConsultation}>
            <Icon name="ClipboardCheck" />
            {t('detail.firstConsultation')}
          </Button>
        )}
        {hasTreatmentPlans === false ? null : (
          <Button variant="outline" size="chip" onClick={onTreatmentPlan}>
            <Icon name="Clipboard" />
            {t('detail.treatmentPlan')}
          </Button>
        )}
        {hasSessionNotes === false ? null : (
          <Button variant="primary" size="chip" onClick={onSessionNote}>
            <Icon name="FilePlus" />
            {t('sessionNote.add')}
          </Button>
        )}
        <Button variant="outline" size="chip" onClick={onEdit}>
          <Icon name="Edit" /> {t('detail.edit')}
        </Button>
        <Button
          variant="destructiveGhost"
          size="chip"
          className="border border-zooborder"
          onClick={onArchive}
        >
          <Icon name="Trash" /> {t('detail.archive')}
        </Button>
      </div>

      <div className="flex [@container(min-width:56rem)]:hidden items-start justify-end gap-2">
        {hasSessionNotes === false ? null : (
          <Button variant="primary" size="chip" onClick={onSessionNote}>
            <Icon name="FilePlus" />
            {t('sessionNote.add')}
          </Button>
        )}
        <HeaderActionsOverflowMenu actions={overflowActions} />
      </div>
    </div>
  );
}
